import { useState, useCallback, useEffect, useMemo } from 'react';
import { PLANS as ALL_PLANS, existingReportPath } from '../../../common/constants/reportPlans';
import SelectReportTable from '../SelectReportTable/SelectReportTable';
import styles from './SelectReportPage.module.scss';
import ReportInProgressModal from '../../../components/vinCode/ReportInProgress';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import LoadingModal from '../../../components/vinCode/loadingModal';
import InsufficientCreditsModal from '../../../components/vinCode/insufficientCreditsModal';
import CreditUsageModal from '../../../components/vinCode/creditUsageModal';
import ViewExistingReportModal from '../../../components/vinCode/viewExistingReport';
import { LOCAL_STORAGE_KEYS } from '../../../common/constants/vinCode';
import { routesPath } from '../../../common/constants/routesPath';
import { ReportReusageCodes } from '../../../common/constants/reportReuseCodes';
import { useCreditsManagement } from '../../../common/hooks/useCreditsManagement';
import { useVinCodeService } from '../../../services/vinCodeService';
import { useCreditsService } from '../../../services/creditsService';
import { useVehiclePhoto } from './useVehiclePhoto';
import { useCredits } from '../../../context/CreditsContext';
import { v4 as uuidv4 } from 'uuid';
import VinBadge from '../VinBadge/VinBadge';
import { useImpersonation } from '../../../hooks/useImpersonation';
import { ReadOnlyNotice } from '../../../components/impersonation/ReadOnlyNotice';
import { useUpgradeOffer } from '../../../hooks/useUpgradeOffer';
import { UPGRADE_ERROR_CODES, useUpgradeService } from '../../../services/upgradeService';
import UpgradeConfirmModal from '../../../components/upgrade/UpgradeConfirmModal';

export default function SelectReportPage({ onPlanSelect }) {
    const { i18n, t } = useTranslation(['reportPlans', 'profile', 'upgrade']);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // no plan until the user picks one: ordering is disabled until then
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [vinCode, setVinCode] = useState('');
    const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
    const [isProcessing, setIsProcessing] = useState(false);
    // a read-only impersonation never orders a report or spends the user's credits
    const { isImpersonating } = useImpersonation();

    const [modalVisibility, setModalVisibility] = useState({
        viewExistingReportModal: false,
        reportInProgressModal: false,
    });
    const [jobUuid, setJobUuid] = useState(null);
    const [error, setError] = useState(null);

    const { createReport } = useVinCodeService();
    const { getUserCredits } = useCreditsService();
    const { updateReport, updateCredits } = useCredits();

    const {
        currentCredits,
        isLoading,
        isInsufficientModalVisible,
        isConfirmationModalVisible,
        setIsLoading,
        setIsConfirmationModalVisible,
        setIsInsufficientModalVisible,
        checkCredits,
    } = useCreditsManagement(getUserCredits);

    // SC-QA-27 / SC-QA-15: a user with a Standard order for this VIN gets Complete as an upgrade of that order, for 1
    // credit and without a new dealer order
    const isExternalOnlyFlow = searchParams.get('externalOnly') === 'true';
    const { upgradeToComplete } = useUpgradeService();
    const { offer: upgradeOffer, reload: reloadUpgradeOffer } = useUpgradeOffer(vinCode, {
        enabled: !isExternalOnlyFlow && !isImpersonating,
    });
    const canUpgrade = upgradeOffer?.canUpgrade === true;
    // SC-QA-55: the upgrade spends credits only from its own confirm dialog; null while that dialog is closed
    const [upgradeConfirm, setUpgradeConfirm] = useState(null);
    const activePlans = useMemo(
        () =>
            canUpgrade
                ? ALL_PLANS.map(p =>
                      // the singular unit ("credit") is the Standard plan's
                      p.id === 'complete'
                          ? { ...p, price: upgradeOffer.price, currencyKey: 'plans.standard.currency' }
                          : p
                  )
                : ALL_PLANS,
        [canUpgrade, upgradeOffer]
    );
    const selectedPlanPrice = activePlans.find(p => p.id === selectedPlan)?.price || 1;
    // the external-only flow never shows the plans table
    const vehicle = useVehiclePhoto(vinCode, { enabled: searchParams.get('externalOnly') !== 'true' });

    useEffect(() => {
        const vinCodeParam = searchParams.get('vinCode');
        const isExternalOnly = searchParams.get('externalOnly') === 'true';

        if (vinCodeParam) {
            setVinCode(decodeURIComponent(vinCodeParam));
        }

        if (isExternalOnly) {
            setSelectedPlan('external');

            if (!isImpersonating) {
                checkCredits(1);
            }
        }
    }, [searchParams, isImpersonating]);

    const handleSelect = useCallback(
        id => {
            setSelectedPlan(id);
            onPlanSelect?.(id);
        },
        [onPlanSelect]
    );

    const handleSendCode = async (checkReportReusage = true) => {
        if (isImpersonating) {
            return;
        }

        setIsConfirmationModalVisible(false);
        setIsLoading(true);
        setIsProcessing(true);

        try {
            const response = await createReport(
                vinCode,
                i18n.language.slice(0, 2),
                idempotencyKey,
                checkReportReusage,
                selectedPlan
            );

            if (response.status === 201) {
                updateCredits(currentCredits - selectedPlanPrice);

                await new Promise(resolve => setTimeout(resolve, 2000));

                // Navigate to the report
                //navigate(`/report/${response.data.jobUuid}`);
                navigate('/dashboard');
            }
            return response;
        } catch (error) {
            console.error('Error sending the vin code:', error);
            if (!error.response?.data?.code) {
                throw error;
            }

            handleReportReusageError(error);
        } finally {
            setIdempotencyKey(uuidv4());
            setIsLoading(false);
            setIsProcessing(false);
        }
    };

    // the balance is read fresh so the dialog shows what will be left; too few credits sends the user to buy more
    const askToConfirmUpgrade = async offer => {
        try {
            const credits = await getUserCredits();
            if (credits < offer.price) {
                setIsInsufficientModalVisible(true);
                return;
            }
            setUpgradeConfirm({ jobUuid: offer.jobUuid, price: offer.price, credits });
        } catch (creditsError) {
            console.error('Error checking credits:', creditsError.message);
            setError(t('upgrade:banner.error'));
        }
    };

    const confirmUpgrade = async () => {
        if (isImpersonating || !upgradeConfirm || isProcessing) {
            return;
        }

        const { jobUuid: upgradeJobUuid, price, credits } = upgradeConfirm;
        setUpgradeConfirm(null);
        setIsLoading(true);
        setIsProcessing(true);

        try {
            const upgraded = await upgradeToComplete(upgradeJobUuid);
            updateCredits(credits - price);
            navigate(existingReportPath('complete', upgraded.vin || vinCode, upgraded.uuid));
        } catch (upgradeError) {
            if (upgradeError.response?.data?.code) {
                handleReportReusageError(upgradeError);
            } else {
                setError(t('upgrade:banner.error'));
            }
        } finally {
            setIsLoading(false);
            setIsProcessing(false);
        }
    };

    const handleReportReusageError = error => {
        const { code: errorCode, message, jobUuid, vinCode: errorVinCode } = error.response?.data;

        const errorMessage = message || 'An error occurred while sending the vin code.';

        if (!errorCode) {
            setError(errorMessage);
            return;
        }

        const { DENY_CREATION_24_HOURS, VIEW_EXISTING_REPORT, REPORT_IN_PROGRESS } = ReportReusageCodes;

        switch (errorCode) {
            // the offer was not loaded yet when Complete was ordered: load it and confirm the 1-credit upgrade instead
            case UPGRADE_ERROR_CODES.EXISTING_ORDER:
                reloadUpgradeOffer().then(next => {
                    if (next?.canUpgrade) {
                        askToConfirmUpgrade(next);
                    } else {
                        setError(t('upgrade:planPage.unavailable'));
                    }
                });
                break;

            case UPGRADE_ERROR_CODES.ALREADY_COMPLETE:
                navigate(existingReportPath('complete', vinCode, upgradeOffer?.jobUuid || jobUuid));
                break;

            case UPGRADE_ERROR_CODES.NO_INTERNAL_DATA:
            case UPGRADE_ERROR_CODES.NOT_POSSIBLE:
                setError(t('upgrade:planPage.unavailable'));
                break;

            case DENY_CREATION_24_HOURS:
                setError(t('profile:form.vinValidation.already-requested-24-hours'));
                break;

            case VIEW_EXISTING_REPORT:
                setJobUuid(jobUuid);
                setVinCode(errorVinCode);
                setModalVisibility(prev => ({ ...prev, viewExistingReportModal: true }));
                break;

            case REPORT_IN_PROGRESS:
                setVinCode(errorVinCode);
                setModalVisibility(prev => ({ ...prev, reportInProgressModal: true }));
                break;

            default:
                setError(errorMessage);
        }
    };

    const handleCreateNewReport = modalToClose => {
        setModalVisibility(prev => ({ ...prev, [modalToClose]: false }));
        handleSendCode(false);
    };

    const handleConfirmationModalCancel = () => {
        setIsConfirmationModalVisible(false);
    };

    const handleViewExistingReport = jobUuid => {
        setModalVisibility(prev => ({ ...prev, viewExistingReportModal: false }));
        navigate(existingReportPath(selectedPlan, vinCode, jobUuid));
    };

    const handleInsufficientCreditsConfirm = () => {
        if (isImpersonating) {
            return;
        }

        localStorage.setItem(LOCAL_STORAGE_KEYS.PAYMENT_COMPLETED, 'true');
        setIsConfirmationModalVisible(false);
        navigate(`/payment?vincode=${vinCode}`);
    };

    const handleProceedToOrder = () => {
        if (!vinCode) {
            setError(t('profile:form.vinValidation.required'));
            return;
        }

        const plan = activePlans.find(p => p.id === selectedPlan);

        if (!plan) {
            return;
        }

        if (plan.id === 'complete' && canUpgrade) {
            askToConfirmUpgrade(upgradeOffer);
            return;
        }

        checkCredits(plan.price);
    };

    const handleCancelAndExit = useCallback(() => {
        setIsConfirmationModalVisible(false);
        setIsInsufficientModalVisible(false);

        if (searchParams.get('externalOnly') === 'true') {
            navigate(`/dashboard?vinCode=${encodeURIComponent(vinCode)}`);
        }
    }, [searchParams, navigate, vinCode, setIsConfirmationModalVisible, setIsInsufficientModalVisible]);

    const isExternalOnly = searchParams.get('externalOnly') === 'true';

    // while impersonating no credit check runs, so the external-only flow shows the page instead of its loader
    if (
        isExternalOnly &&
        !isImpersonating &&
        !isConfirmationModalVisible &&
        !isInsufficientModalVisible &&
        !isLoading
    ) {
        return <LoadingModal isVisible={true} code={vinCode} />;
    }

    return (
        <section className={styles.page}>
            {searchParams.get('externalOnly') !== 'true' && (
                <>
                    <header className={styles.header}>
                        <VinBadge vin={vinCode} label="VIN Code" />
                    </header>
                    <div className={styles.tableWrap}>
                        <SelectReportTable
                            selectedPlan={selectedPlan}
                            onSelect={handleSelect}
                            plans={activePlans}
                            vehicle={vehicle}
                        />
                    </div>
                </>
            )}

            {canUpgrade && !isExternalOnlyFlow && <p className={styles.footerText}>{t('upgrade:planPage.note')}</p>}

            {error && <div className={styles.errorMessage}>{error}</div>}

            <ReadOnlyNotice />

            {searchParams.get('externalOnly') !== 'true' && (
                <footer className={styles.footer}>
                    <p className={styles.footerText}>
                        {t('reportPlans:sampleLinks.text')}{' '}
                        <Link to={routesPath.PUBLIC.SAMPLE_REPORT} target="_blank" rel="noopener noreferrer">
                            {t('reportPlans:sampleLinks.standard')}
                        </Link>
                        {' · '}
                        <Link to={routesPath.PUBLIC.COMBINED_REPORT_EXAMPLE} target="_blank" rel="noopener noreferrer">
                            {t('reportPlans:sampleLinks.complete')}
                        </Link>
                    </p>
                    <button
                        className={styles.proceedBtn}
                        onClick={handleProceedToOrder}
                        disabled={!selectedPlan || isImpersonating || isProcessing || isLoading}
                    >
                        {t('reportPlans:orderButton')}
                    </button>
                </footer>
            )}

            {isLoading && <LoadingModal isVisible={isLoading} code={vinCode} />}

            <InsufficientCreditsModal
                isVisible={isInsufficientModalVisible}
                onConfirm={handleInsufficientCreditsConfirm}
                onCancel={handleCancelAndExit}
            />

            <CreditUsageModal
                isVisible={isConfirmationModalVisible}
                onCancel={handleCancelAndExit}
                onConfirm={() => handleSendCode(true)}
                currentCredits={currentCredits}
                price={selectedPlanPrice}
                samplePath={
                    selectedPlan === 'complete'
                        ? routesPath.PUBLIC.COMBINED_REPORT_EXAMPLE
                        : routesPath.PUBLIC.SAMPLE_REPORT
                }
            />

            <UpgradeConfirmModal
                isVisible={!!upgradeConfirm}
                price={upgradeConfirm?.price}
                currentCredits={upgradeConfirm?.credits}
                onCancel={() => setUpgradeConfirm(null)}
                onConfirm={confirmUpgrade}
            />

            <ViewExistingReportModal
                price={selectedPlanPrice}
                isVisible={modalVisibility.viewExistingReportModal}
                onCancel={() => handleCreateNewReport('viewExistingReportModal')}
                onClose={() => setModalVisibility(prev => ({ ...prev, viewExistingReportModal: false }))}
                onConfirm={() => handleViewExistingReport(jobUuid)}
            />

            <ReportInProgressModal
                price={selectedPlanPrice}
                isVisible={modalVisibility.reportInProgressModal}
                onCancel={() => handleCreateNewReport('reportInProgressModal')}
                onClose={() => setModalVisibility(prev => ({ ...prev, reportInProgressModal: false }))}
                onConfirm={() => setModalVisibility(prev => ({ ...prev, reportInProgressModal: false }))}
            />
        </section>
    );
}
